import { inject, Injectable,  } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { LoginGuardService } from '@services/auth/loginGuard.service';
import { TermsAcceptanceService } from '@services/terms/terms-acceptance.service';

/**
 * Guard que protege las rutas autenticadas del sistema.
 *
 * Verifica dos condiciones antes de permitir el acceso:
 * 1. El usuario tiene un token y rol válidos en localStorage (`LoginGuardService.accessLogin`).
 * 2. El usuario ha aceptado la versión vigente de los términos de privacidad (`TermsAcceptanceService.hasAccepted`).
 *
 * Si el token existe pero los términos no fueron aceptados (ej: recarga durante el modal),
 * limpia los datos de sesión y redirige al login para forzar un nuevo flujo de aceptación.
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
    private readonly _router      = inject(Router);
    private readonly _loginGuard  = inject(LoginGuardService);
    private readonly _terms       = inject(TermsAcceptanceService);

    /**
     * Evalúa si la ruta puede activarse.
     * @returns `Observable<true>` si autenticado y términos aceptados, `Observable<false>` en caso contrario.
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        if (this._loginGuard.accessLogin()) {
          const userId = localStorage.getItem('idUser') ?? '';
          if (!this._terms.hasAccepted(userId)) {
            // Sesión válida pero términos pendientes de aceptar (ej: recarga durante el modal).
            // Se limpia la sesión para forzar un nuevo login donde aparecerá el modal.
            ['token', 'refreshToken', 'idUser', 'username', 'role'].forEach(k =>
              localStorage.removeItem(k)
            );
            void this._router.navigate(['auth/login']);
            return of(false);
          }
          return of(true);
        }
        void this._router.navigate(['auth/login']);
        return of(false);
    }
}
